/**
 *  @swagger
 *  definitions:
 *    RegattaRequirementParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaRequirement
 *          type: string
 *        Content:
 *          description: Content of the RegattaRequirement
 *          type: string
 *        Type:
 *          description: Type of the RegattaRequirement
 *          $ref: '#/components/enums/RegattaRequirementType'
 *        Source:
 *          description: Source of the RegattaRequirement
 *          type: string
 *        Required:
 *          description: Required of the RegattaRequirement
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    RegattaRequirementCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaRequirementParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *            - Content
 *            - Type
 *            - Required
 *
 *  @swagger
 *  definitions:
 *    RegattaRequirementQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaRequirement
 *              type: string
 *        - $ref: '#/definitions/RegattaRequirementParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaRequirementUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaRequirementQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaRequirement:
 *      allOf:
 *        - $ref: '#/definitions/RegattaRequirementCreateParams'
 *        - $ref: '#/definitions/RegattaRequirementUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaRequirement:
 *        allOf:
 *          - $ref: '#/definitions/RegattaRequirement'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaRequirement
 *                $ref: '#/components/schemas/Regatta'
 */

// Enums
import {
  RegattaRequirementType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Content: string;
  Name: string;
  Required: boolean;
  Source: null | string;
  Type: RegattaRequirementType;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Required: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Source: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaRequirementType)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaRequirement'> => {
  const RegattaRequirement = stack.define(
    'RegattaRequirement',
    fields(Sequelize),
    {
      tableName: 'RegattaRequirements',
    },
  );

  RegattaRequirement.associate = (models: $ModelClasses) => {
    models.RegattaRequirement.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaRequirement.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaRequirement;
});
